import { useCallback } from 'react';
import { Input, Label, Button } from '@artstorefronts/ui';
import { Plus, Trash2 } from 'lucide-react';

export type RegistryConfigField = {
  key: string;
  type: string;
  label: string;
  description?: string | null;
  default: unknown;
  min?: number | null;
  max?: number | null;
  maxLength?: number | null;
  choices?: unknown | null;
};

function omitKey(obj: Record<string, unknown>, key: string): Record<string, unknown> {
  const rest = { ...obj };
  delete rest[key];
  return rest;
}

function isOptionsItem(x: unknown): x is { id: string; label: string } {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.id === 'string' && typeof o.label === 'string';
}

export function StepConfigForm({
  stepCode,
  schema,
  value,
  onChange,
}: {
  stepCode: string;
  schema: RegistryConfigField[];
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const setOverride = useCallback(
    (key: string, nextVal: unknown, fieldDefault: unknown) => {
      if (JSON.stringify(nextVal) === JSON.stringify(fieldDefault)) {
        onChange(omitKey(value, key));
      } else {
        onChange({ ...value, [key]: nextVal });
      }
    },
    [value, onChange]
  );

  if (schema.length === 0) {
    return <p className="text-sm text-gray-500">No configurable options for this step.</p>;
  }

  return (
    <div className="space-y-4 border-t border-gray-100 pt-3 mt-1">
      {schema.map((field) => {
        const hasOverride = field.key in value;
        const effective = hasOverride ? value[field.key] : field.default;

        if (field.type === 'number') {
          const n = typeof effective === 'number' ? effective : Number(effective) || 0;
          return (
            <div key={field.key}>
              <Label className="block text-sm mb-1">{field.label}</Label>
              {field.description ? <p className="text-xs text-gray-500 mb-1">{field.description}</p> : null}
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  className="w-32 text-sm"
                  value={Number.isFinite(n) ? n : ''}
                  min={field.min ?? undefined}
                  max={field.max ?? undefined}
                  onChange={(e) => {
                    const v = e.target.value === '' ? NaN : Number(e.target.value);
                    if (!Number.isFinite(v)) return;
                    setOverride(field.key, v, field.default);
                  }}
                />
                <span className="text-xs text-gray-400">default: {String(field.default)}</span>
              </div>
            </div>
          );
        }

        if (field.type === 'text') {
          const s = typeof effective === 'string' ? effective : String(effective ?? '');
          return (
            <div key={field.key}>
              <Label className="block text-sm mb-1">{field.label}</Label>
              <Input
                type="text"
                className="w-full text-sm"
                maxLength={field.maxLength ?? undefined}
                value={s}
                onChange={(e) => setOverride(field.key, e.target.value, field.default)}
              />
            </div>
          );
        }

        if (field.type === 'select') {
          const choices = Array.isArray(field.choices) ? (field.choices as { id: string; label: string }[]) : [];
          const sel = typeof effective === 'string' ? effective : String(field.default);
          return (
            <div key={field.key}>
              <Label className="block text-sm mb-1">{field.label}</Label>
              <select
                className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm"
                value={sel}
                onChange={(e) => setOverride(field.key, e.target.value, field.default)}
              >
                {choices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          );
        }

        if (field.type === 'multiselect') {
          const choices = Array.isArray(field.choices) ? (field.choices as { id: string; label: string }[]) : [];
          const selected = new Set(Array.isArray(effective) ? effective.map(String) : []);
          const defArr = Array.isArray(field.default) ? field.default.map(String) : [];
          return (
            <div key={field.key}>
              <Label className="block text-sm mb-1">{field.label}</Label>
              {field.description ? <p className="text-xs text-gray-500 mb-2">{field.description}</p> : null}
              <div className="flex flex-wrap gap-3">
                {choices.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) next.add(c.id);
                        else next.delete(c.id);
                        const arr = [...next];
                        const norm = (a: string[]) => JSON.stringify([...a].sort());
                        if (norm(arr) === norm(defArr)) {
                          onChange(omitKey(value, field.key));
                        } else {
                          onChange({ ...value, [field.key]: arr });
                        }
                      }}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
          );
        }

        if (field.type === 'options') {
          const rows = Array.isArray(effective) ? effective.filter(isOptionsItem) : [];
          const defaultRows = Array.isArray(field.default) ? field.default.filter(isOptionsItem) : [];

          const updateRows = (nextRows: { id: string; label: string }[]) => {
            if (JSON.stringify(nextRows) === JSON.stringify(defaultRows)) {
              onChange(omitKey(value, field.key));
            } else {
              onChange({ ...value, [field.key]: nextRows });
            }
          };

          return (
            <div key={field.key}>
              <Label className="block text-sm mb-1">{field.label}</Label>
              <p className="text-xs text-gray-500 mb-2">Each row needs a stable id and display label.</p>
              <div className="space-y-2">
                {rows.map((row, idx) => (
                  <div key={`${stepCode}-${field.key}-${idx}`} className="flex gap-2 items-center">
                    <Input
                      className="text-sm flex-1"
                      placeholder="id"
                      value={row.id}
                      onChange={(e) => {
                        const next = [...rows];
                        next[idx] = { ...row, id: e.target.value };
                        updateRows(next);
                      }}
                    />
                    <Input
                      className="text-sm flex-[2]"
                      placeholder="Label"
                      value={row.label}
                      onChange={(e) => {
                        const next = [...rows];
                        next[idx] = { ...row, label: e.target.value };
                        updateRows(next);
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Remove option"
                      onClick={() => updateRows(rows.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="font-normal"
                  onClick={() => updateRows([...rows, { id: '', label: '' }])}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add option
                </Button>
              </div>
            </div>
          );
        }

        return (
          <div key={field.key} className="text-xs text-amber-700">
            Unsupported field type &quot;{field.type}&quot; for {field.key}
          </div>
        );
      })}
    </div>
  );
}
